/**
 * Editor theming.
 *
 * Every colour is a `--fs-*` custom property rather than a literal, so the
 * editor follows the app's light/dark switch without CodeMirror being told
 * about it — the values simply resolve differently once `data-theme` changes on
 * the root element. Only `dark` is passed in, and only because CodeMirror needs
 * it to pick its own internal defaults.
 */
import { EditorView } from '@codemirror/view';
import type { Extension } from '@codemirror/state';

export interface EditorThemeOptions {
  readonly dark: boolean;
  readonly fontSize: number;
  readonly fontFamily: string;
}

export function flowScriptTheme(options: EditorThemeOptions): Extension {
  const { dark, fontSize, fontFamily } = options;

  return EditorView.theme(
    {
      '&': {
        color: 'var(--fs-ink)',
        backgroundColor: 'var(--fs-surface)',
        fontSize: `${fontSize}px`,
        height: '100%',
      },
      '.cm-scroller': {
        fontFamily,
        lineHeight: '1.6',
        overflow: 'auto',
      },
      '.cm-content': {
        caretColor: 'var(--fs-accent)',
        padding: '12px 0 40vh 0',
      },
      '.cm-line': { padding: '0 12px 0 6px' },

      '&.cm-focused': { outline: 'none' },
      '.cm-cursor, .cm-dropCursor': { borderLeftColor: 'var(--fs-accent)', borderLeftWidth: '2px' },
      '&.cm-focused .cm-selectionBackgroundm .cm-selectionBackground, .cm-content ::selection': {
        backgroundColor: 'color-mix(in srgb, var(--fs-accent) 26%, transparent)',
      },
      '.cm-selectionBackground': {
        backgroundColor: 'color-mix(in srgb, var(--fs-accent) 20%, transparent)',
      },

      '.cm-gutters': {
        backgroundColor: 'var(--fs-surface)',
        color: 'var(--fs-ink-faint)',
        border: 'none',
        borderRight: '1px solid var(--fs-border)',
      },
      '.cm-activeLineGutter': {
        backgroundColor: 'var(--fs-surface-sunken)',
        color: 'var(--fs-ink-muted)',
      },
      '.cm-activeLine': { backgroundColor: 'color-mix(in srgb, var(--fs-accent) 6%, transparent)' },
      '.cm-foldGutter span': { color: 'var(--fs-ink-faint)' },

      '.cm-matchingBracket, &.cm-focused .cm-matchingBracket': {
        backgroundColor: 'color-mix(in srgb, var(--fs-accent) 26%, transparent)',
        outline: '1px solid var(--fs-accent)',
        color: 'inherit',
      },
      '.cm-nonmatchingBracket': { color: 'var(--fs-danger)' },

      '.cm-searchMatch': {
        backgroundColor: 'color-mix(in srgb, var(--fs-warning) 34%, transparent)',
        outline: '1px solid var(--fs-warning)',
      },
      '.cm-searchMatch.cm-searchMatch-selected': {
        backgroundColor: 'color-mix(in srgb, var(--fs-accent) 40%, transparent)',
      },
      '.cm-selectionMatch': {
        backgroundColor: 'color-mix(in srgb, var(--fs-accent) 16%, transparent)',
      },

      '.cm-panels': {
        backgroundColor: 'var(--fs-surface-raised)',
        color: 'var(--fs-ink)',
        borderTop: '1px solid var(--fs-border)',
      },
      '.cm-panel input, .cm-panel button': {
        backgroundColor: 'var(--fs-surface)',
        color: 'var(--fs-ink)',
        border: '1px solid var(--fs-border)',
        borderRadius: '5px',
        padding: '2px 6px',
      },

      '.cm-tooltip': {
        backgroundColor: 'var(--fs-surface-raised)',
        color: 'var(--fs-ink)',
        border: '1px solid var(--fs-border)',
        borderRadius: '8px',
        boxShadow: 'var(--shadow-pop, 0 8px 24px rgba(0,0,0,.2))',
        overflow: 'hidden',
      },
      '.cm-tooltip-autocomplete > ul > li': { padding: '4px 10px' },
      '.cm-tooltip-autocomplete > ul > li[aria-selected]': {
        backgroundColor: 'var(--fs-accent)',
        color: 'var(--fs-accent-ink)',
      },
      '.cm-completionIcon': { opacity: 0.7 },
      '.cm-completionDetail': { color: 'var(--fs-ink-faint)', fontStyle: 'normal' },

      // Diagnostics: a wavy underline, never colour alone — the severity is
      // also carried by the gutter marker and the problems panel.
      '.cm-lintRange-error': {
        backgroundImage: 'none',
        textDecoration: 'underline wavy var(--fs-danger)',
        textUnderlineOffset: '3px',
      },
      '.cm-lintRange-warning': {
        backgroundImage: 'none',
        textDecoration: 'underline wavy var(--fs-warning)',
        textUnderlineOffset: '3px',
      },
      '.cm-lintRange-info': {
        backgroundImage: 'none',
        textDecoration: 'underline dotted var(--fs-info)',
        textUnderlineOffset: '3px',
      },
      '.cm-diagnostic': { padding: '6px 10px', borderLeftWidth: '4px' },
      '.cm-diagnostic-error': { borderLeftColor: 'var(--fs-danger)' },
      '.cm-diagnostic-warning': { borderLeftColor: 'var(--fs-warning)' },
      '.cm-diagnostic-info': { borderLeftColor: 'var(--fs-info)' },
      '.cm-diagnosticSource': { color: 'var(--fs-ink-faint)', fontSize: '85%' },
      '.cm-lint-marker-error': { content: 'none' },
    },
    { dark },
  );
}
